import { Link, useLocation } from 'react-router'
import {
	getSectionForPath,
	isPageActive,
	type Section,
} from '#app/utils/site-sections.ts'
import { cn } from '#app/utils/misc.tsx'

export type SiblingAnchor = { href: string; label: string }

type Props = {
	/** Optional in-page anchors shown below the sibling page list.
	 *  Use on long info pages so users can jump to sections. */
	anchors?: SiblingAnchor[]
	/** Override section detection (rarely needed). */
	section?: Section
	className?: string
}

/**
 * Sibling-page rail. Lists the other pages in the current section, with the
 * active page highlighted. Optionally appends an in-page anchor list for long
 * pages.
 *
 * Lives in the right column on desktop (via SectionLayout) and at the bottom
 * of the content on mobile.
 */
export function SiblingNav({ anchors, section: sectionProp, className }: Props) {
	const { pathname } = useLocation()
	const section = sectionProp ?? getSectionForPath(pathname)

	if (!section || section.pages.length === 0) {
		// Nothing useful to show: empty section or unknown route.
		return null
	}

	return (
		<nav
			aria-label="Tästä osiosta"
			className={cn('text-sm', className)}
		>
			<SectionList section={section} pathname={pathname} />

			{anchors && anchors.length > 0 ? (
				<>
					<div className="border-border my-6 border-t" />
					<AnchorList anchors={anchors} />
				</>
			) : null}
		</nav>
	)
}

function SectionList({
	section,
	pathname,
}: {
	section: Section
	pathname: string
}) {
	return (
		<>
			<div className="text-muted-foreground font-mono text-xs font-semibold tracking-widest uppercase">
				Tästä osiosta
			</div>
			<ul className="mt-3 space-y-px">
				{section.pages.map((page) => {
					const active = isPageActive(pathname, page)
					return (
						<li key={page.to}>
							<Link
								to={page.to}
								aria-current={active ? 'page' : undefined}
								className={cn(
									'group flex items-baseline gap-2 py-1.5 transition-colors',
									active
										? 'text-primary font-semibold'
										: 'text-foreground/80 hover:text-primary',
								)}
							>
								<span
									aria-hidden
									className={cn(
										'inline-block w-3 shrink-0 font-mono',
										active ? 'text-primary' : 'text-transparent',
									)}
								>
									▸
								</span>
								<span>{page.label}</span>
							</Link>
						</li>
					)
				})}
			</ul>
		</>
	)
}

function AnchorList({ anchors }: { anchors: SiblingAnchor[] }) {
	return (
		<>
			<div className="text-muted-foreground font-mono text-xs font-semibold tracking-widest uppercase">
				Sivulla
			</div>
			<ul className="mt-3 space-y-px">
				{anchors.map((a) => (
					<li key={a.href}>
						<a
							href={a.href}
							className="text-foreground/80 hover:text-primary flex items-baseline gap-2 py-1.5 transition-colors"
						>
							<span aria-hidden className="text-muted-foreground inline-block w-3 shrink-0 font-mono">
								·
							</span>
							<span>{a.label}</span>
						</a>
					</li>
				))}
			</ul>
		</>
	)
}
